import React, { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  useDroppable
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, GripVertical, Trash2, Edit2, X, Calendar, Archive, Move } from 'lucide-react';
import ArchiveButton from './ArchiveButton';
import { reorderColumns } from '../utils/columnUtils';

// Sortable Column Container
const SortableColumn = ({ 
  id, 
  column, 
  tasks, 
  onEditColumn, 
  onDeleteColumn, 
  onAddTask, 
  onEditTask, 
  onDeleteTask, 
  onAssign, 
  onUpdateDate, 
  getSubtaskProgress,
  isDragging = false
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ 
    id: id,
    data: {
      type: 'column',
      column
    }
  });

  const isDraggingThis = isDragging || isSortableDragging;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 250ms cubic-bezier(0.25, 0.8, 0.25, 1)',
    opacity: isDraggingThis ? 0.4 : 1,
    scale: isDraggingThis ? '0.98' : '1',
    filter: isDraggingThis ? 'blur(1px)' : 'none',
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`min-w-[320px] max-w-[320px] transition-all duration-250 ${
        isDraggingThis ? 'z-0' : 'z-10'
      }`}
    >
      <KanbanColumn
        column={column}
        tasks={tasks}
        onEditColumn={onEditColumn}
        onDeleteColumn={onDeleteColumn}
        onAddTask={onAddTask}
        onEditTask={onEditTask}
        onDeleteTask={onDeleteTask}
        onAssign={onAssign}
        onUpdateDate={onUpdateDate}
        getSubtaskProgress={getSubtaskProgress}
        columnDragProps={{ attributes, listeners }}
        isDragging={isDraggingThis}
      />
    </div>
  );
};

// Draggable Task Card
const TaskCard = ({ task, onEdit, onDelete, onAssign, onUpdateDate, getSubtaskProgress, isDragging = false }) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ 
    id: task._id,
    data: {
      type: 'task',
      task
    }
  });

  const isDraggingThis = isDragging || isSortableDragging;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 250ms cubic-bezier(0.25, 0.8, 0.25, 1)',
    opacity: isDraggingThis ? 0.6 : 1,
    cursor: isDraggingThis ? 'grabbing' : 'grab',
    scale: isDraggingThis ? '1.02' : '1',
  };

  const handleDateChange = (e) => {
    if (onUpdateDate) {
      onUpdateDate(task._id, e.target.value);
    }
    setShowDatePicker(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`bg-white border rounded-lg p-3 mb-3 shadow-sm transition-all cursor-grab active:cursor-grabbing group ${
        isDraggingThis 
          ? 'border-[#1C3E53] shadow-2xl ring-2 ring-[#1C3E53]/20' 
          : 'border-gray-200 hover:shadow-md hover:border-gray-300'
      }`}
      onClick={(e) => {
        // Prevent drag when clicking on buttons
        if (e.target.closest('button')) {
          e.stopPropagation();
        }
      }}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2 flex-1">
          <h4 className="font-medium text-gray-900 flex-1">{task.task}</h4>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(task);
            }}
            className="text-gray-400 hover:text-blue-500 p-1 rounded hover:bg-blue-50 transition-colors"
            title="Edit task"
          >
            <Edit2 className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Archive this task?')) {
                console.log('Archive task:', task._id);
              }
            }}
            className="text-gray-400 hover:text-[#1C3E53] p-1 rounded hover:bg-blue-50 transition-colors"
            title="Archive task"
          >
            <Archive className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
                onDelete(task._id);
              }
            }}
            className="text-gray-400 hover:text-red-500 p-1 rounded hover:bg-red-50 transition-colors"
            title="Delete task"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
      <div 
        className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          onAssign && onAssign(task);
        }}
      >
        {/* Multiple Assignees Display */}
        {task.assignees && task.assignees.length > 0 ? (
          <div className="flex items-center">
            <div className="flex -space-x-1">
              {task.assignees.slice(0, 3).map((assignee, idx) => (
                <div
                  key={idx}
                  className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium border-2 border-white"
                  style={{backgroundColor: assignee.color}}
                  title={assignee.name}
                >
                  {assignee.avatar}
                </div>
              ))}
              {task.assignees.length > 3 && (
                <div className="w-6 h-6 rounded-full flex items-center justify-center bg-gray-300 text-gray-700 text-xs font-medium border-2 border-white">
                  +{task.assignees.length - 3}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium"
              style={{backgroundColor: task.person?.color || '#CCCCCC'}}
            >
              {task.person?.avatar || 'UN'}
            </div>
            <span className="text-gray-600 text-xs">{task.person?.name || 'Unassigned'}</span>
          </div>
        )}
        {task.date && (
          <div 
            className="flex items-center space-x-1 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setShowDatePicker(!showDatePicker);
            }}
          >
            <Calendar className="h-3 w-3 text-gray-400" />
            <span className="text-xs text-gray-600 font-medium">
              {new Date(task.date).toLocaleDateString('fr-FR')}
            </span>
          </div>
        )}
      </div>
      
      {/* Subtasks Progress */}
      {(() => {
        const progress = getSubtaskProgress(task);
        return progress.total > 0 ? (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Subtasks</span>
              <span className="text-xs text-gray-500">{progress.completed}/{progress.total}</span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all duration-300"
                style={{width: `${progress.percentage}%`}}
              />
            </div>
          </div>
        ) : null;
      })()}
      
      {showDatePicker && (
        <div className="mt-2 pt-2 border-t">
          <Input
            type="date"
            value={task.date}
            onChange={handleDateChange}
            onBlur={() => setShowDatePicker(false)}
            autoFocus
            className="text-xs"
          />
        </div>
      )}
    </div>
  );
};

// Kanban Column Component
const KanbanColumn = ({ 
  column, 
  tasks, 
  onEditColumn, 
  onDeleteColumn, 
  onAddTask, 
  onEditTask, 
  onDeleteTask, 
  onAssign, 
  onUpdateDate, 
  getSubtaskProgress,
  columnDragProps,
  isDragging = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState(column.label);
  const [newTaskName, setNewTaskName] = useState('');
  const [showAddTask, setShowAddTask] = useState(false);

  const { setNodeRef, isOver } = useDroppable({
    id: column.label,
    data: {
      type: 'column',
    },
  });

  const handleSaveName = () => {
    if (editingName.trim() && editingName !== column.label) {
      onEditColumn(column.label, editingName.trim(), column.color);
    }
    setIsEditing(false);
  };

  const handleAddTask = () => {
    if (newTaskName.trim()) {
      onAddTask(column.label, newTaskName.trim());
      setNewTaskName('');
      setShowAddTask(false);
    }
  };

  const isDoneColumn = column.label?.toLowerCase() === 'done' || 
                      column.label?.toLowerCase() === 'fini' || 
                      column.value === 'done';

  return (
    <div 
      ref={setNodeRef}
      className={`bg-white rounded-xl border-2 shadow-lg transition-all duration-300 ease-in-out ${
        isOver 
          ? 'border-[#1C3E53] bg-gradient-to-b from-blue-50 to-blue-100/50 shadow-2xl scale-[1.02] ring-2 ring-[#1C3E53]/30' 
          : isDragging
            ? 'border-gray-300 opacity-70 scale-95'
            : 'border-gray-200 hover:border-gray-300 hover:shadow-xl'
      }`}
      style={{ height: 'fit-content', minHeight: '500px' }}
    >
      {/* Column Header with vibrant colors */}
      <div 
        className="border-b-4 p-4 rounded-t-xl"
        style={{ 
          backgroundColor: `${column.color}20`,
          borderBottomColor: column.color
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2 flex-1">
            {/* Drag handle for column */}
            {!isDoneColumn && (
              <div 
                {...columnDragProps.attributes}
                {...columnDragProps.listeners}
                className="cursor-grab active:cursor-grabbing p-1.5 hover:bg-white/50 hover:scale-110 rounded-lg transition-all duration-200"
                title="⬌ Glisser pour réorganiser la colonne"
              >
                <Move className="h-5 w-5 text-gray-700" />
              </div>
            )}
            
            {isEditing ? (
              <Input
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') setIsEditing(false);
                }}
                onBlur={handleSaveName}
                autoFocus
                className="text-sm font-semibold flex-1"
              />
            ) : (
              <h3 
                className="font-bold text-gray-900 flex-1 cursor-pointer hover:opacity-70 transition-opacity text-base"
                onClick={() => setIsEditing(true)}
                title="Cliquer pour modifier le nom"
              >
                {column.label}
              </h3>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <span 
              className="text-xs px-3 py-1 rounded-full font-bold text-white"
              style={{ backgroundColor: column.color }}
            >
              {tasks.length}
            </span>
            {!isDoneColumn && (
              <button
                onClick={() => onDeleteColumn(column.label)}
                className="text-gray-600 hover:text-red-600 p-1.5 rounded hover:bg-white/50 transition-colors"
                title="Supprimer la colonne"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tasks Container */}
      <div className="p-4 min-h-[400px] bg-gradient-to-b from-white to-gray-50">
        <SortableContext items={tasks.map(t => t._id)} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <TaskCard
              key={task._id}
              task={task}
              onEdit={onEditTask}
              onDelete={onDeleteTask}
              onAssign={onAssign}
              onUpdateDate={onUpdateDate}
              getSubtaskProgress={getSubtaskProgress}
            />
          ))}
        </SortableContext>
        
        {/* Add Task */}
        <div className="mt-3">
          {showAddTask ? (
            <div className="bg-white border-2 border-dashed border-[#1C3E53] rounded-lg p-3">
              <Input
                placeholder="Task name..."
                value={newTaskName}
                onChange={(e) => setNewTaskName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
                className="mb-2 text-sm"
                autoFocus
              />
              <div className="flex space-x-2">
                <Button size="sm" onClick={handleAddTask} className="bg-[#1C3E53] hover:bg-[#2A5470]">
                  Add
                </Button>
                <Button size="sm" variant="ghost" onClick={() => {
                  setShowAddTask(false);
                  setNewTaskName('');
                }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddTask(true)}
              className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#1C3E53] hover:text-[#1C3E53] transition-all text-sm font-medium"
            >
              <Plus className="h-4 w-4 mr-2 inline" />
              Add Task
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Kanban Board Component
const KanbanBoard = ({ 
  columns, 
  tasks, 
  onEditColumn, 
  onDeleteColumn, 
  onAddColumn, 
  onTaskMove, 
  onAddTaskToColumn, 
  onEditTask, 
  onDeleteTask, 
  onAssignTask, 
  onUpdateTaskDate, 
  getSubtaskProgress,
  onColumnReorder 
}) => {
  const [activeId, setActiveId] = useState(null);
  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnColor, setNewColumnColor] = useState('#4A90A4');
  const [showAddColumn, setShowAddColumn] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Column reordering (only for sortable columns, Done is excluded)
    if (activeData?.type === 'column') {
      const activeIndex = sortableColumns.findIndex(col => col.label === active.id);
      const overIndex = sortableColumns.findIndex(col => col.label === over.id);
      
      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        // Reorder only the sortable columns
        let newSortableColumns = arrayMove(sortableColumns, activeIndex, overIndex);
        
        // Reconstruct full columns array with Done at the end
        let newColumns = [...newSortableColumns];
        if (doneColumn) {
          newColumns.push(doneColumn);
        }
        
        // Update order indices
        newColumns = newColumns.map((col, idx) => ({ ...col, order: idx }));
        
        // Call parent handler
        if (onColumnReorder) {
          onColumnReorder(newColumns);
        }
      }
      return;
    }

    // Task moving between columns
    if (activeData?.type === 'task') {
      const task = activeData.task;
      const currentStatus = task.status.label;
      const newStatus = overData?.type === 'column' ? over.id : currentStatus;
      
      if (currentStatus !== newStatus) {
        const newStatusObj = columns.find(col => col.label === newStatus);
        if (newStatusObj && onTaskMove) {
          onTaskMove(task._id, newStatusObj);
        }
      }
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleAddColumn = () => {
    if (newColumnName.trim()) {
      onAddColumn(newColumnName, newColumnColor);
      setNewColumnName('');
      setNewColumnColor('#4A90A4');
      setShowAddColumn(false);
    }
  };

  const activeTask = activeId && tasks.find(t => t._id === activeId);
  const activeColumn = activeId && columns.find(c => c.label === activeId);

  // Helper to check if a column is the Done column
  const isDoneColumn = (label) => {
    return label?.toLowerCase() === 'done' || 
           label?.toLowerCase() === 'fini' || 
           label?.toLowerCase() === 'terminé';
  };

  // Get sortable columns (all except Done)
  const sortableColumns = columns.filter(col => !isDoneColumn(col.label));
  const doneColumn = columns.find(col => isDoneColumn(col.label));

  return (
    <div className="flex flex-col h-full">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* Columns with horizontal sorting - excludes Done column */}
        <SortableContext items={sortableColumns.map(c => c.label)} strategy={horizontalListSortingStrategy}>
          <div className="flex gap-6 overflow-x-auto pb-4 px-2">
            {/* Render sortable columns */}
            {sortableColumns.map((column) => {
              const columnTasks = tasks
                .filter(task => task.status.label === column.label)
                .sort((a, b) => (a.order || 0) - (b.order || 0));
              
              return (
                <SortableColumn
                  key={column.label}
                  id={column.label}
                  column={column}
                  tasks={columnTasks}
                  onEditColumn={onEditColumn}
                  onDeleteColumn={onDeleteColumn}
                  onAddTask={onAddTaskToColumn}
                  onEditTask={onEditTask}
                  onDeleteTask={onDeleteTask}
                  onAssign={onAssignTask}
                  onUpdateDate={onUpdateTaskDate}
                  getSubtaskProgress={getSubtaskProgress}
                />
              );
            })}

            {/* Render Done column separately (not sortable) */}
            {doneColumn && (
              <div className="min-w-[320px] max-w-[320px]">
                <KanbanColumn
                  column={doneColumn}
                  tasks={tasks
                    .filter(task => task.status.label === doneColumn.label)
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                  }
                  onEditColumn={onEditColumn}
                  onDeleteColumn={onDeleteColumn}
                  onAddTask={onAddTaskToColumn}
                  onEditTask={onEditTask}
                  onDeleteTask={onDeleteTask}
                  onAssign={onAssignTask}
                  onUpdateDate={onUpdateTaskDate}
                  getSubtaskProgress={getSubtaskProgress}
                  columnDragProps={{ attributes: {}, listeners: {} }}
                  isDragging={false}
                />
              </div>
            )}

            {/* Add Column */}
            <div className="min-w-[320px] max-w-[320px]">
              {showAddColumn ? (
                <div className="bg-white border-2 border-[#1C3E53] rounded-xl p-6 shadow-lg">
                  <Label className="text-sm font-medium mb-2 block">Column Name</Label>
                  <Input
                    placeholder="e.g., In Review"
                    value={newColumnName}
                    onChange={(e) => setNewColumnName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddColumn()}
                    className="mb-3"
                    autoFocus
                  />
                  
                  <Label className="text-sm font-medium mb-2 block">Color</Label>
                  <div className="flex space-x-2 mb-4">
                    {['#1C3E53', '#4A90A4', '#2D7A4C', '#C07F00', '#B83A2A', '#6BA5B8', '#9333EA', '#DC2626'].map(color => (
                      <button
                        key={color}
                        onClick={() => setNewColumnColor(color)}
                        className={`w-8 h-8 rounded-full transition-all ${
                          newColumnColor === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'
                        }`}
                        style={{backgroundColor: color}}
                      />
                    ))}
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button size="sm" onClick={handleAddColumn} className="bg-[#1C3E53] hover:bg-[#2A5470]">
                      Add Column
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => {
                      setShowAddColumn(false);
                      setNewColumnName('');
                    }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowAddColumn(true)}
                  className="w-full h-32 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-[#1C3E53] hover:text-[#1C3E53] hover:bg-gray-50 transition-all flex flex-col items-center justify-center space-y-2"
                >
                  <Plus className="h-8 w-8" />
                  <span className="font-medium">Add Column</span>
                </button>
              )}
            </div>
          </div>
        </SortableContext>

        {/* Drag Overlay - Enhanced with smooth animations */}
        <DragOverlay
          dropAnimation={{
            duration: 300,
            easing: 'cubic-bezier(0.25, 0.8, 0.25, 1)',
          }}
        >
          {activeTask && (
            <div className="transform scale-105 rotate-3 shadow-2xl">
              <TaskCard
                task={activeTask}
                onEdit={() => {}}
                onDelete={() => {}}
                onAssign={() => {}}
                onUpdateDate={() => {}}
                getSubtaskProgress={getSubtaskProgress}
                isDragging
              />
            </div>
          )}
          {activeColumn && (
            <div className="bg-white border-2 border-[#1C3E53] rounded-xl p-6 shadow-2xl min-w-[320px] transform scale-105 rotate-1 ring-4 ring-[#1C3E53]/20 backdrop-blur-sm">
              <div className="flex items-center space-x-2 mb-2">
                <Move className="h-5 w-5 text-[#1C3E53] animate-pulse" />
                <h3 className="font-bold text-lg text-gray-900">{activeColumn.label}</h3>
              </div>
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full animate-pulse"
                  style={{ backgroundColor: activeColumn.color }}
                />
                <span className="text-sm text-gray-600 font-medium">
                  {tasks.filter(t => t.status.label === activeColumn.label).length} tâche{tasks.filter(t => t.status.label === activeColumn.label).length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default KanbanBoard;
